import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { 
  Building2, Plus, Trash2, Users, Loader2, Settings, UserPlus, 
  CheckCircle2, XCircle, X, KeyRound, ChevronDown, ChevronUp, 
  Pencil, Eye, DoorOpen, Layers, Phone, User, MoreVertical, Power,
  Home, UtensilsCrossed, Search, Briefcase, Truck, ChefHat
} from 'lucide-react';
import { toast } from 'sonner';

export const StructuresPage = () => {
  const { api, enterStructure } = useAuth();
  const navigate = useNavigate();
  const [structures, setStructures] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [expandedStructures, setExpandedStructures] = useState({});

  // Dialog states
  const [structureDialogOpen, setStructureDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [structureToDelete, setStructureToDelete] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [setPasswordDialogOpen, setSetPasswordDialogOpen] = useState(false);
  const [structureForPassword, setStructureForPassword] = useState(null);
  const [newDeletePassword, setNewDeletePassword] = useState('');
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [addUserToStructureId, setAddUserToStructureId] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserRole, setEditUserRole] = useState('');

  // Form states
  const [structureForm, setStructureForm] = useState({ 
    name: '', code: '', floors: '', delete_password: '',
    contact1_name: '', contact1_phone: '',
    contact2_name: '', contact2_phone: '',
    is_multi_house: false,
    houses: [], // [{name, prefix, floors, requires_meal_service}]
    production_type: 'own' // 'own', 'catering', 'both'
  });
  const [addUserForm, setAddUserForm] = useState({ 
    username: '', password: '', role: 'operatore' 
  });
  
  // House form for multi-house structures
  const [houseForm, setHouseForm] = useState({
    name: '', prefix: '', floors: '', rooms_input: '', requires_meal_service: true
  });
  const [addHouseDialogOpen, setAddHouseDialogOpen] = useState(false);
  const [addHouseToStructureId, setAddHouseToStructureId] = useState(null);

  // Contacts edit dialog
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [contactsStructure, setContactsStructure] = useState(null);
  const [contactsForm, setContactsForm] = useState({
    contact1_name: '', contact1_phone: '',
    contact2_name: '', contact2_phone: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  
  // Organizations for assignment
  const [organizations, setOrganizations] = useState([]);
  const [assignOrgDialogOpen, setAssignOrgDialogOpen] = useState(false);
  const [structureForOrg, setStructureForOrg] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  
  // Production type dialog
  const [productionTypeDialogOpen, setProductionTypeDialogOpen] = useState(false);
  const [structureForProductionType, setStructureForProductionType] = useState(null);
  const [selectedProductionType, setSelectedProductionType] = useState('own');
  
  // Catering relation dialog
  const [cateringRelationDialogOpen, setCateringRelationDialogOpen] = useState(false);
  const [cateringStructures, setCateringStructures] = useState([]);
  const [selectedCateringId, setSelectedCateringId] = useState('');
  const [satelliteForCatering, setSatelliteForCatering] = useState(null);

  const roleLabels = {
    super_admin: 'Super Admin',
    supervisore: 'Supervisore',
    admin_struttura: 'Admin',
    operatore: 'Operatore',
    cucina: 'Cucina'
  };

  const roleColors = {
    admin_struttura: 'bg-blue-100 text-blue-700',
    operatore: 'bg-green-100 text-green-700',
    cucina: 'bg-orange-100 text-orange-700'
  };
  
  const productionTypeLabels = {
    own: 'Produzione Propria',
    catering: 'Solo Catering',
    both: 'Produzione + Catering'
  };

  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [structuresRes, usersRes] = await Promise.all([
        api.get('/structures'),
        api.get('/users')
      ]);
      setStructures(structuresRes.data);
      setUsers(usersRes.data);
      
      // Fetch organizations only for super admin
      if (isSuperAdmin) {
        try {
          const orgsRes = await api.get('/organizations');
          setOrganizations(orgsRes.data);
        } catch (e) {
          console.error('Error fetching organizations:', e);
        }
      }
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [api, isSuperAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Handlers
  const handleEnterStructure = (structure) => {
    enterStructure(structure);
    navigate('/');
    toast.success(`Gestendo: ${structure.name}`);
  };

  const handleCreateStructure = async (e) => {
    e.preventDefault();
    if (!structureForm.name || !structureForm.code) {
      toast.error('Nome e codice obbligatori');
      return;
    }
    if (!structureForm.delete_password || structureForm.delete_password.length < 4) {
      toast.error('Password eliminazione obbligatoria (min 4 caratteri)');
      return;
    }
    
    // Validazione multi-casa
    if (structureForm.is_multi_house) {
      if (structureForm.houses.length === 0) {
        toast.error('Aggiungi almeno una casa per una struttura multi-casa');
        return;
      }
    } else {
      if (!structureForm.floors) {
        toast.error('Inserisci almeno un piano');
        return;
      }
    }
    
    try {
      // Build contacts array
      const reference_contacts = [];
      if (structureForm.contact1_name || structureForm.contact1_phone) {
        reference_contacts.push({ name: structureForm.contact1_name, phone: structureForm.contact1_phone });
      }
      if (structureForm.contact2_name || structureForm.contact2_phone) {
        reference_contacts.push({ name: structureForm.contact2_name, phone: structureForm.contact2_phone });
      }
      
      let payload = {
        name: structureForm.name,
        code: structureForm.code.toLowerCase().replace(/\s+/g, '-'),
        delete_password: structureForm.delete_password,
        reference_contacts,
        is_multi_house: structureForm.is_multi_house,
        production_type: structureForm.production_type
      };
      
      if (structureForm.is_multi_house) {
        // Multi-casa: invia le case con rooms_config già parsato
        payload.houses = structureForm.houses.map(h => ({
          name: h.name,
          prefix: h.prefix.toUpperCase(),
          floors: typeof h.floors === 'string' ? h.floors.split(',').map(f => f.trim()).filter(Boolean) : h.floors,
          rooms_config: h.rooms_config || {},
          requires_meal_service: h.requires_meal_service
        }));
        payload.floors = [];
        payload.rooms_config = {};
      } else {
        // Singola-casa
        payload.floors = structureForm.floors.split(',').map(f => f.trim()).filter(Boolean);
        payload.rooms_config = {};
        payload.houses = [];
      }
      
      const response = await api.post('/structures', payload);
      await api.post(`/structures/${response.data.id}/init-defaults`);
      toast.success('Struttura creata');
      setStructureDialogOpen(false);
      setStructureForm({ 
        name: '', code: '', floors: '', delete_password: '', 
        contact1_name: '', contact1_phone: '', contact2_name: '', contact2_phone: '',
        is_multi_house: false, houses: []
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };
  
  // Helper function per parsare le camere da una stringa
  const parseRoomsInput = (input, floors) => {
    if (!input || !input.trim()) return {};
    
    const rooms_config = {};
    floors.forEach(f => rooms_config[f] = []);
    
    const parts = input.split(',').map(p => p.trim()).filter(Boolean);
    for (const part of parts) {
      if (part.includes('-')) {
        const [start, end] = part.split('-').map(s => s.trim());
        const startMatch = start.match(/^(\D*)(\d+)$/);
        const endMatch = end.match(/^(\D*)(\d+)$/);
        
        if (startMatch && endMatch) {
          const prefix = startMatch[1] || '';
          const startNum = parseInt(startMatch[2], 10);
          const endNum = parseInt(endMatch[2], 10);
          const numLength = startMatch[2].length;
          
          for (let i = startNum; i <= endNum; i++) {
            const room = prefix + String(i).padStart(numLength, '0');
            // Assegna al piano in base al primo carattere della camera
            const floorChar = room.charAt(0);
            if (floors.includes(floorChar)) {
              if (!rooms_config[floorChar].includes(room)) {
                rooms_config[floorChar].push(room);
              }
            } else if (floors.length > 0) {
              // Se non trova il piano, metti nel primo
              if (!rooms_config[floors[0]].includes(room)) {
                rooms_config[floors[0]].push(room);
              }
            }
          }
        }
      } else {
        const floorChar = part.charAt(0);
        if (floors.includes(floorChar)) {
          if (!rooms_config[floorChar].includes(part)) {
            rooms_config[floorChar].push(part);
          }
        } else if (floors.length > 0) {
          if (!rooms_config[floors[0]].includes(part)) {
            rooms_config[floors[0]].push(part);
          }
        }
      }
    }
    
    // Ordina le camere in ogni piano
    Object.keys(rooms_config).forEach(floor => {
      rooms_config[floor].sort();
    });
    
    return rooms_config;
  };
  
  // Aggiungi casa temporanea al form
  const handleAddHouseToForm = () => {
    if (!houseForm.name || !houseForm.prefix || !houseForm.floors) {
      toast.error('Nome, prefisso e piani sono obbligatori');
      return;
    }
    if (houseForm.prefix.length > 2) {
      toast.error('Il prefisso deve essere massimo 2 caratteri');
      return;
    }
    if (structureForm.houses.some(h => h.prefix.toUpperCase() === houseForm.prefix.toUpperCase())) {
      toast.error('Prefisso già utilizzato');
      return;
    }
    
    const floorsArray = houseForm.floors.split(',').map(f => f.trim()).filter(Boolean);
    const rooms_config = parseRoomsInput(houseForm.rooms_input, floorsArray);
    
    setStructureForm({
      ...structureForm,
      houses: [...structureForm.houses, { 
        ...houseForm, 
        prefix: houseForm.prefix.toUpperCase(),
        rooms_config 
      }]
    });
    setHouseForm({ name: '', prefix: '', floors: '', rooms_input: '', requires_meal_service: true });
    toast.success('Casa aggiunta');
  };
  
  // Rimuovi casa dal form
  const handleRemoveHouseFromForm = (index) => {
    setStructureForm({
      ...structureForm,
      houses: structureForm.houses.filter((_, i) => i !== index)
    });
  };
  
  // Aggiungi casa a struttura esistente
  const handleAddHouseToExisting = async () => {
    if (!houseForm.name || !houseForm.prefix || !houseForm.floors) {
      toast.error('Nome, prefisso e piani sono obbligatori');
      return;
    }
    try {
      const floorsArray = houseForm.floors.split(',').map(f => f.trim()).filter(Boolean);
      const rooms_config = parseRoomsInput(houseForm.rooms_input, floorsArray);
      
      await api.post(`/structures/${addHouseToStructureId}/houses`, {
        name: houseForm.name,
        prefix: houseForm.prefix.toUpperCase(),
        floors: floorsArray,
        rooms_config,
        requires_meal_service: houseForm.requires_meal_service
      });
      toast.success('Casa aggiunta');
      setAddHouseDialogOpen(false);
      setAddHouseToStructureId(null);
      setHouseForm({ name: '', prefix: '', floors: '', rooms_input: '', requires_meal_service: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteStructure = async () => {
    if (!structureToDelete || !deletePassword) {
      toast.error('Inserisci la password');
      return;
    }
    try {
      await api.delete(`/structures/${structureToDelete.id}`, {
        data: { delete_password: deletePassword }
      });
      toast.success('Struttura eliminata');
      setDeleteDialogOpen(false);
      setStructureToDelete(null);
      setDeletePassword('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const openDeleteDialog = async (structure) => {
    try {
      const response = await api.get(`/structures/${structure.id}/has-delete-password`);
      if (!response.data.has_delete_password) {
        toast.error('Imposta prima la password di eliminazione');
        setStructureForPassword(structure);
        setSetPasswordDialogOpen(true);
        return;
      }
    } catch (error) {
      console.error('Error checking delete password:', error);
      toast.error('Errore nel controllo password');
      return;
    }
    setStructureToDelete(structure);
    setDeletePassword('');
    setDeleteDialogOpen(true);
  };

  const handleSetDeletePassword = async () => {
    if (!structureForPassword || newDeletePassword.length < 4) {
      toast.error('Password minimo 4 caratteri');
      return;
    }
    try {
      await api.post(`/structures/${structureForPassword.id}/set-delete-password`, {
        delete_password: newDeletePassword
      });
      toast.success('Password impostata');
      setSetPasswordDialogOpen(false);
      setStructureForPassword(null);
      setNewDeletePassword('');
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleToggleActive = async (structure) => {
    try {
      await api.put(`/structures/${structure.id}`, { is_active: !structure.is_active });
      toast.success(structure.is_active ? 'Disattivata' : 'Attivata');
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Open contacts edit dialog
  const openContactsDialog = (structure) => {
    const contacts = structure.reference_contacts || [];
    setContactsStructure(structure);
    setContactsForm({
      contact1_name: contacts[0]?.name || '',
      contact1_phone: contacts[0]?.phone || '',
      contact2_name: contacts[1]?.name || '',
      contact2_phone: contacts[1]?.phone || ''
    });
    setContactsDialogOpen(true);
  };

  // Save contacts
  const handleSaveContacts = async () => {
    if (!contactsStructure) return;
    try {
      const reference_contacts = [];
      if (contactsForm.contact1_name || contactsForm.contact1_phone) {
        reference_contacts.push({ name: contactsForm.contact1_name, phone: contactsForm.contact1_phone });
      }
      if (contactsForm.contact2_name || contactsForm.contact2_phone) {
        reference_contacts.push({ name: contactsForm.contact2_name, phone: contactsForm.contact2_phone });
      }
      await api.put(`/structures/${contactsStructure.id}`, { reference_contacts });
      toast.success('Contatti salvati');
      setContactsDialogOpen(false);
      setContactsStructure(null);
      fetchData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleConfigureRooms = (structure) => {
    setSelectedStructure(structure);
    setConfigDialogOpen(true);
  };

  const handleSaveRoomConfig = async (floors, roomsConfig, houseId = null) => {
    if (!selectedStructure) return;
    try {
      if (selectedStructure.is_multi_house && houseId) {
        // Save to specific house
        await api.put(`/structures/${selectedStructure.id}/houses/${houseId}`, { 
          floors, 
          rooms_config: roomsConfig 
        });
      } else {
        // Save to single-casa structure
        await api.put(`/structures/${selectedStructure.id}`, { floors, rooms_config: roomsConfig });
      }
      toast.success('Configurazione salvata');
      setConfigDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  // User handlers
  const getStructureUsers = (structureId) => users.filter(u => u.structure_id === structureId);
  
  const handleAddUserToStructure = async (e) => {
    e.preventDefault();
    if (!addUserForm.username || !addUserForm.password) {
      toast.error('Compila tutti i campi');
      return;
    }
    try {
      await api.post('/auth/register', {
        ...addUserForm,
        structure_id: addUserToStructureId
      });
      toast.success('Utente creato');
      setAddUserToStructureId(null);
      setAddUserForm({ username: '', password: '', role: 'operatore' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleUpdateUserRole = async () => {
    if (!editingUser || !editUserRole) return;
    try {
      await api.put(`/users/${editingUser.id}`, { role: editUserRole });
      toast.success('Ruolo aggiornato');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleToggleUserActive = async (user) => {
    try {
      await api.put(`/users/${user.id}`, { is_active: !user.is_active });
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Eliminare ${user.username}?`)) return;
    try {
      await api.delete(`/users/${user.id}`);
      toast.success('Eliminato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Handle organization assignment
  const handleAssignOrganization = async () => {
    if (!structureForOrg) return;
    
    try {
      await api.put(`/structures/${structureForOrg.id}`, {
        organization_id: selectedOrgId || null
      });
      toast.success(selectedOrgId ? 'Organizzazione assegnata' : 'Organizzazione rimossa');
      setAssignOrgDialogOpen(false);
      setStructureForOrg(null);
      setSelectedOrgId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const openAssignOrgDialog = (structure) => {
    setStructureForOrg(structure);
    setSelectedOrgId(structure.organization_id || '');
    setAssignOrgDialogOpen(true);
  };

  // Handle production type change
  const handleChangeProductionType = async () => {
    if (!structureForProductionType) return;
    
    try {
      await api.put(`/structures/${structureForProductionType.id}`, {
        production_type: selectedProductionType
      });
      toast.success('Tipo produzione aggiornato');
      setProductionTypeDialogOpen(false);
      setStructureForProductionType(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const openProductionTypeDialog = (structure) => {
    setStructureForProductionType(structure);
    setSelectedProductionType(structure.production_type || 'own');
    setProductionTypeDialogOpen(true);
  };

  // Handle catering relation
  const openCateringRelationDialog = (structure) => {
    setSatelliteForCatering(structure);
    // Get catering structures
    const cateringOnes = structures.filter(s => 
      (s.production_type === 'catering' || s.production_type === 'both') && 
      s.id !== structure.id
    );
    setCateringStructures(cateringOnes);
    setSelectedCateringId('');
    setCateringRelationDialogOpen(true);
  };

  const handleCreateCateringRelation = async () => {
    if (!selectedCateringId || !satelliteForCatering) return;
    
    try {
      await api.post('/catering/relations', {
        catering_structure_id: selectedCateringId,
        satellite_structure_id: satelliteForCatering.id
      });
      toast.success('Relazione catering creata');
      setCateringRelationDialogOpen(false);
      setSatelliteForCatering(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Helper functions
  const getTotalRooms = (structure) => 
    Object.values(structure.rooms_config || {}).flat().length;

  const formatNumber = (num, singular, plural) => 
    `${num} ${num === 1 ? singular : plural}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="structures-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Strutture</h1>
          <p className="text-slate-500">{formatNumber(structures.length, 'struttura', 'strutture')} registrate</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca struttura..."
              className="pl-9 w-48 sm:w-64"
              data-testid="search-structures"
            />
          </div>
          <Dialog open={structureDialogOpen} onOpenChange={setStructureDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-700 hover:bg-blue-800" data-testid="add-structure-btn">
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Nuova Struttura</span>
                <span className="sm:hidden">Nuova</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nuova Struttura</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateStructure} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    value={structureForm.name}
                    onChange={(e) => setStructureForm({ ...structureForm, name: e.target.value })}
                    placeholder="RSA Villa Serena"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Codice</Label>
                  <Input
                    value={structureForm.code}
                    onChange={(e) => setStructureForm({ ...structureForm, code: e.target.value })}
                    placeholder="rsa-villa-serena"
                  />
                </div>
              </div>
              
              {/* Tipo struttura */}
              <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                <Label className="text-base font-medium">Tipo Struttura</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      checked={!structureForm.is_multi_house}
                      onChange={() => setStructureForm({ ...structureForm, is_multi_house: false, houses: [] })}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm">Singola-Casa</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      checked={structureForm.is_multi_house}
                      onChange={() => setStructureForm({ ...structureForm, is_multi_house: true, floors: '' })}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm">Multi-Casa</span>
                  </label>
                </div>
              </div>
              
              {/* Singola-casa: piani */}
              {!structureForm.is_multi_house && (
                <div className="space-y-2">
                  <Label>Piani (separati da virgola)</Label>
                  <Input
                    value={structureForm.floors}
                    onChange={(e) => setStructureForm({ ...structureForm, floors: e.target.value })}
                    placeholder="1, 2, 3"
                  />
                </div>
              )}
              
              {/* Multi-casa: lista case */}
              {structureForm.is_multi_house && (
                <div className="space-y-3">
                  <Label className="text-base font-medium">Case</Label>
                  
                  {/* Case già aggiunte */}
                  {structureForm.houses.length > 0 && (
                    <div className="space-y-2">
                      {structureForm.houses.map((house, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${house.requires_meal_service ? 'bg-green-100' : 'bg-slate-100'}`}>
                              <Home className={`w-4 h-4 ${house.requires_meal_service ? 'text-green-600' : 'text-slate-500'}`} />
                            </div>
                            <div>
                              <div className="font-medium text-sm">{house.name}</div>
                              <div className="text-xs text-slate-500">
                                Prefisso: {house.prefix} • Piani: {house.floors}
                                {!house.requires_meal_service && ' • Appartamenti'}
                              </div>
                            </div>
                          </div>
                          <Button 
                            type="button"
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-red-500 hover:bg-red-50"
                            onClick={() => handleRemoveHouseFromForm(idx)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Form per aggiungere casa */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                    <div className="text-sm font-medium text-blue-700">Aggiungi Casa</div>
                    <div className="grid grid-cols-3 gap-3">
                      <Input
                        value={houseForm.name}
                        onChange={(e) => setHouseForm({ ...houseForm, name: e.target.value })}
                        placeholder="Nome (es. Edificio A)"
                      />
                      <Input
                        value={houseForm.prefix}
                        onChange={(e) => setHouseForm({ ...houseForm, prefix: e.target.value.toUpperCase().slice(0, 2) })}
                        placeholder="Prefisso (A, B...)"
                        maxLength={2}
                      />
                      <Input
                        value={houseForm.floors}
                        onChange={(e) => setHouseForm({ ...houseForm, floors: e.target.value })}
                        placeholder="Piani (1, 2, 3)"
                      />
                    </div>
                    <div>
                      <Input
                        value={houseForm.rooms_input}
                        onChange={(e) => setHouseForm({ ...houseForm, rooms_input: e.target.value })}
                        placeholder="Camere (es: 101-110 o 101, 102, 103)"
                        className="text-sm"
                      />
                      <p className="text-xs text-slate-500 mt-1">Inserisci numeri separati da virgola o usa il trattino per un intervallo</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={houseForm.requires_meal_service}
                          onChange={(e) => setHouseForm({ ...houseForm, requires_meal_service: e.target.checked })}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <UtensilsCrossed className="w-4 h-4 text-slate-500" />
                        <span className="text-sm">Servizio pasti (comande)</span>
                      </label>
                      <Button 
                        type="button" 
                        size="sm"
                        onClick={handleAddHouseToForm}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Aggiungi
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Password eliminazione</Label>
                <Input
                  type="password"
                  value={structureForm.delete_password}
                  onChange={(e) => setStructureForm({ ...structureForm, delete_password: e.target.value })}
                  placeholder="Minimo 4 caratteri"
                />
              </div>
              <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800">
                Crea
              </Button>
            </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Structures Grid */}
      <div className="grid gap-4">
        {structures.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Building2 className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">Nessuna struttura</p>
              <p className="text-sm text-slate-400">Crea la prima struttura per iniziare</p>
            </CardContent>
          </Card>
        ) : (
          structures
            .filter(s => !searchQuery || s.name.toLowerCase().includes(searchQuery.toLowerCase()) || s.code.toLowerCase().includes(searchQuery.toLowerCase()))
            .map(structure => {
            const structureUsers = getStructureUsers(structure.id);
            const activeUsers = structureUsers.filter(u => u.is_active !== false);
            const totalRooms = getTotalRooms(structure);
            const isExpanded = expandedStructures[structure.id];

            return (
              <Card 
                key={structure.id} 
                className={`overflow-hidden transition-all ${!structure.is_active ? 'opacity-60' : ''}`}
                data-testid={`structure-${structure.id}`}
              >
                <CardContent className="p-0">
                  {/* Main Info Row */}
                  <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
                    {/* Icon + Name row for mobile */}
                    <div className="flex items-center gap-3 sm:gap-5">
                      {/* Icon */}
                      <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center shrink-0 ${
                        structure.is_active ? 'bg-blue-100' : 'bg-slate-100'
                      }`}>
                        <Building2 className={`w-6 h-6 sm:w-7 sm:h-7 ${structure.is_active ? 'text-blue-700' : 'text-slate-400'}`} />
                      </div>

                      {/* Name and code - mobile friendly */}
                      <div className="flex-1 min-w-0 sm:hidden">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 
                            className={`font-semibold text-base truncate ${
                              structure.is_active 
                                ? 'text-slate-900 hover:text-blue-700 cursor-pointer transition-colors' 
                                : 'text-slate-400'
                            }`}
                            onClick={() => structure.is_active && handleEnterStructure(structure)}
                          >
                            {structure.name}
                          </h3>
                          {structure.is_active ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                          ) : (
                            <XCircle className="w-4 h-4 text-slate-400 shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-slate-500 font-mono truncate">{structure.code}</p>
                      </div>
                    </div>

                    {/* Info - desktop */}
                    <div className="flex-1 min-w-0 hidden sm:block">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 
                          className={`font-semibold text-lg truncate ${
                            structure.is_active 
                              ? 'text-slate-900 hover:text-blue-700 cursor-pointer transition-colors' 
                              : 'text-slate-400'
                          }`}
                          onClick={() => structure.is_active && handleEnterStructure(structure)}
                        >
                          {structure.name}
                        </h3>
                        {structure.is_active ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-slate-400 shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-slate-500 font-mono mb-1">{structure.code}</p>
                      
                      {/* Organization badge */}
                      {structure.organization_name && (
                        <div className="mb-2">
                          <Badge className="bg-purple-100 text-purple-700 text-xs">
                            <Briefcase className="w-3 h-3 mr-1" />
                            {structure.organization_name}
                          </Badge>
                        </div>
                      )}
                      
                      {/* Production type badge */}
                      {structure.production_type && structure.production_type !== 'own' && (
                        <div className="mb-3">
                          <Badge className={
                            structure.production_type === 'catering' 
                              ? 'bg-emerald-100 text-emerald-700 text-xs'
                              : 'bg-amber-100 text-amber-700 text-xs'
                          }>
                            <Truck className="w-3 h-3 mr-1" />
                            {productionTypeLabels[structure.production_type] || structure.production_type}
                          </Badge>
                        </div>
                      )}
                      
                      {/* Stats */}
                      <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm">
                        {/* Tipo struttura */}
                        {structure.is_multi_house ? (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">
                            Multi-Casa ({structure.houses?.length || 0})
                          </Badge>
                        ) : (
                          <>
                            <Badge className="bg-slate-100 text-slate-600 text-xs">
                              Singola-Casa
                            </Badge>
                            <div className="flex items-center gap-1.5 text-slate-600">
                              <Layers className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400" />
                              <span>{structure.floors?.length || 0} piani</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-slate-600">
                              <DoorOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400" />
                              <span>{totalRooms} camere</span>
                            </div>
                          </>
                        )}
                        <div className="flex items-center gap-1.5 text-slate-600">
                          <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400" />
                          <span>{activeUsers.length} utenti</span>
                        </div>
                      </div>
                      
                      {/* Case per strutture multi-casa - con dettagli piani/camere */}
                      {structure.is_multi_house && structure.houses?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {structure.houses.map((house, idx) => {
                            const houseRooms = Object.values(house.rooms_config || {}).reduce((acc, rooms) => acc + (rooms?.length || 0), 0);
                            const houseFloors = house.floors?.length || 0;
                            return (
                              <div 
                                key={idx}
                                className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs ${
                                  house.requires_meal_service 
                                    ? 'bg-green-50 text-green-700 border border-green-200' 
                                    : 'bg-slate-50 text-slate-600 border border-slate-200'
                                }`}
                              >
                                <Home className="w-3.5 h-3.5" />
                                <span className="font-semibold">{house.prefix}</span>
                                <span className="text-slate-500">-</span>
                                <span>{house.name}</span>
                                <span className="text-slate-400">|</span>
                                <span className="text-slate-500">{houseFloors}P {houseRooms}C</span>
                                {!house.requires_meal_service && (
                                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-slate-200 border-0">App.</Badge>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Reference Contacts */}
                      {(structure.reference_contacts?.length > 0) && (
                        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                          {structure.reference_contacts.map((contact, idx) => (
                            contact.name && (
                              <div key={idx} className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg">
                                <User className="w-3.5 h-3.5 text-slate-400" />
                                <span className="font-medium text-slate-700">{contact.name}</span>
                                {contact.phone && (
                                  <>
                                    <Phone className="w-3.5 h-3.5 text-slate-400 ml-2" />
                                    <a href={`tel:${contact.phone}`} className="text-blue-700 hover:underline">{contact.phone}</a>
                                  </>
                                )}
                              </div>
                            )
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Stats - Mobile only */}
                    <div className="flex flex-wrap items-center gap-2 text-xs sm:hidden">
                      {structure.is_multi_house ? (
                        <Badge className="bg-purple-100 text-purple-700 text-[10px]">
                          Multi ({structure.houses?.length || 0})
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 text-[10px]">
                          Singola
                        </Badge>
                      )}
                      <div className="flex items-center gap-1.5 text-slate-600">
                        <Layers className="w-3.5 h-3.5 text-slate-400" />
                        <span>
                          {structure.is_multi_house 
                            ? structure.houses?.reduce((acc, h) => acc + (h.floors?.length || 0), 0) || 0
                            : structure.floors?.length || 0
                          } piani
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-600">
                        <DoorOpen className="w-3.5 h-3.5 text-slate-400" />
                        <span>{totalRooms} cam.</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-600">
                        <Users className="w-3.5 h-3.5 text-slate-400" />
                        <span>{activeUsers.length} utenti</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto justify-end sm:justify-start">
                      <Button
                        size="sm"
                        className="bg-blue-700 hover:bg-blue-800"
                        onClick={() => handleEnterStructure(structure)}
                        disabled={!structure.is_active}
                        data-testid={`enter-${structure.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Gestisci
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openContactsDialog(structure)} title="Contatti di riferimento">
                        <Phone className="w-4 h-4" />
                      </Button>
                      
                      {/* More Actions Menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleConfigureRooms(structure)}>
                            <Settings className="w-4 h-4 mr-2" />
                            Configura Camere
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { 
                            setAddHouseToStructureId(structure.id); 
                            setHouseForm({ name: '', prefix: '', floors: '', requires_meal_service: true });
                            setAddHouseDialogOpen(true); 
                          }}>
                            <Home className="w-4 h-4 mr-2" />
                            Aggiungi Casa
                          </DropdownMenuItem>
                          {isSuperAdmin && (
                            <DropdownMenuItem onClick={() => openAssignOrgDialog(structure)}>
                              <Briefcase className="w-4 h-4 mr-2" />
                              {structure.organization_id ? 'Cambia Organizzazione' : 'Assegna a Organizzazione'}
                            </DropdownMenuItem>
                          )}
                          {isSuperAdmin && (
                            <DropdownMenuItem onClick={() => openProductionTypeDialog(structure)}>
                              <Truck className="w-4 h-4 mr-2" />
                              Tipo Produzione
                            </DropdownMenuItem>
                          )}
                          {isSuperAdmin && (
                            <DropdownMenuItem onClick={() => openCateringRelationDialog(structure)}>
                              <UtensilsCrossed className="w-4 h-4 mr-2" />
                              Collega a Catering
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => { setStructureForPassword(structure); setSetPasswordDialogOpen(true); }}>
                            <KeyRound className="w-4 h-4 mr-2" />
                            Modifica Password
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleActive(structure)}>
                            <Power className="w-4 h-4 mr-2" />
                            {structure.is_active ? 'Disattiva Struttura' : 'Attiva Struttura'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => openDeleteDialog(structure)} 
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Elimina Struttura
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Users Section - Expandable */}
                  <div className="border-t border-slate-100">
                    <button
                      onClick={() => setExpandedStructures(prev => ({ ...prev, [structure.id]: !prev[structure.id] }))}
                      className="w-full px-5 py-3 flex items-center justify-between text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <span className="font-medium">
                          {structureUsers.length === 0 ? 'Nessun utente' : formatNumber(structureUsers.length, 'utente', 'utenti')}
                        </span>
                        {structureUsers.length > 0 && (
                          <span className="text-slate-400">
                            ({structureUsers.filter(u => u.role === 'admin_struttura').length} admin, 
                            {structureUsers.filter(u => u.role === 'operatore').length} operatori, 
                            {structureUsers.filter(u => u.role === 'cucina').length} cucina)
                          </span>
                        )}
                      </span>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>

                    {isExpanded && (
                      <div className="px-5 pb-4 space-y-2">
                        {structureUsers.map(user => (
                          <div 
                            key={user.id} 
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              user.is_active !== false ? 'bg-slate-50' : 'bg-slate-100 opacity-60'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                roleColors[user.role] || 'bg-slate-200 text-slate-600'
                              }`}>
                                {user.username.charAt(0).toUpperCase()}
                              </div>
                              <span className="font-medium text-slate-900">{user.username}</span>
                              <Badge className={`text-xs ${roleColors[user.role] || 'bg-slate-100'}`}>
                                {roleLabels[user.role]}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingUser(user); setEditUserRole(user.role); }}>
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleToggleUserActive(user)}>
                                {user.is_active !== false ? <XCircle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => handleDeleteUser(user)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-2"
                          onClick={() => setAddUserToStructureId(structure.id)}
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Aggiungi utente
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Dialogs */}
      {/* Delete Structure */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Elimina Struttura</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Stai per eliminare <strong>{structureToDelete?.name}</strong> e tutti i dati associati.
            </p>
            <Input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Password di eliminazione"
            />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteDialogOpen(false)}>
                Annulla
              </Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={handleDeleteStructure}>
                Elimina
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Set Password */}
      <Dialog open={setPasswordDialogOpen} onOpenChange={setSetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Password Eliminazione</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Imposta la password per eliminare <strong>{structureForPassword?.name}</strong>
            </p>
            <Input
              type="password"
              value={newDeletePassword}
              onChange={(e) => setNewDeletePassword(e.target.value)}
              placeholder="Minimo 4 caratteri"
            />
            <Button className="w-full bg-blue-700 hover:bg-blue-800" onClick={handleSetDeletePassword}>
              Imposta
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Contacts */}
      <Dialog open={contactsDialogOpen} onOpenChange={setContactsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-blue-700" />
              Contatti di Riferimento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <p className="text-sm text-slate-600">
              Inserisci i contatti delle persone di riferimento per <strong>{contactsStructure?.name}</strong>
            </p>
            
            {/* Contact 1 */}
            <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-slate-700">Referente 1</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Nome</Label>
                  <Input
                    value={contactsForm.contact1_name}
                    onChange={(e) => setContactsForm({ ...contactsForm, contact1_name: e.target.value })}
                    placeholder="Mario Rossi"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Telefono</Label>
                  <Input
                    value={contactsForm.contact1_phone}
                    onChange={(e) => setContactsForm({ ...contactsForm, contact1_phone: e.target.value })}
                    placeholder="+39 333 1234567"
                    type="tel"
                  />
                </div>
              </div>
            </div>
            
            {/* Contact 2 */}
            <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-slate-700">Referente 2</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Nome</Label>
                  <Input
                    value={contactsForm.contact2_name}
                    onChange={(e) => setContactsForm({ ...contactsForm, contact2_name: e.target.value })}
                    placeholder="Anna Verdi"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Telefono</Label>
                  <Input
                    value={contactsForm.contact2_phone}
                    onChange={(e) => setContactsForm({ ...contactsForm, contact2_phone: e.target.value })}
                    placeholder="+39 333 7654321"
                    type="tel"
                  />
                </div>
              </div>
            </div>

            <Button className="w-full bg-blue-700 hover:bg-blue-800" onClick={handleSaveContacts}>
              Salva Contatti
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add User to Structure */}
      <Dialog open={!!addUserToStructureId && !addHouseDialogOpen} onOpenChange={(open) => !open && setAddUserToStructureId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuovo Utente</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddUserToStructure} className="space-y-4">
            <Input
              value={addUserForm.username}
              onChange={(e) => setAddUserForm({ ...addUserForm, username: e.target.value })}
              placeholder="Username"
            />
            <Input
              type="password"
              value={addUserForm.password}
              onChange={(e) => setAddUserForm({ ...addUserForm, password: e.target.value })}
              placeholder="Password"
            />
            <Select value={addUserForm.role} onValueChange={(v) => setAddUserForm({ ...addUserForm, role: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin_struttura">Admin</SelectItem>
                <SelectItem value="operatore">Operatore</SelectItem>
                <SelectItem value="cucina">Cucina</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800">
              Crea
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add House to Existing Structure */}
      <Dialog open={addHouseDialogOpen} onOpenChange={setAddHouseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Home className="w-5 h-5 text-blue-700" />
              Aggiungi Casa
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Aggiungi una nuova casa alla struttura. Diventerà automaticamente multi-casa.
            </p>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Nome Casa</Label>
                <Input
                  value={houseForm.name}
                  onChange={(e) => setHouseForm({ ...houseForm, name: e.target.value })}
                  placeholder="Es. Edificio B, Ala Nord..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Prefisso Camere</Label>
                  <Input
                    value={houseForm.prefix}
                    onChange={(e) => setHouseForm({ ...houseForm, prefix: e.target.value.toUpperCase().slice(0, 2) })}
                    placeholder="B"
                    maxLength={2}
                  />
                  <p className="text-xs text-slate-500">Le camere saranno B101, B102...</p>
                </div>
                <div className="space-y-2">
                  <Label>Piani (separati da virgola)</Label>
                  <Input
                    value={houseForm.floors}
                    onChange={(e) => setHouseForm({ ...houseForm, floors: e.target.value })}
                    placeholder="1, 2, 3"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Camere</Label>
                <Input
                  value={houseForm.rooms_input}
                  onChange={(e) => setHouseForm({ ...houseForm, rooms_input: e.target.value })}
                  placeholder="Es: 101-110 o 101, 102, 103"
                />
                <p className="text-xs text-slate-500">Numeri separati da virgola o usa il trattino per un intervallo</p>
              </div>
              <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={houseForm.requires_meal_service}
                  onChange={(e) => setHouseForm({ ...houseForm, requires_meal_service: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <UtensilsCrossed className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-sm">Servizio Pasti Attivo</span>
                  </div>
                  <p className="text-xs text-slate-500">Disattiva per appartamenti o residenze autonome</p>
                </div>
              </label>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setAddHouseDialogOpen(false);
                  setAddHouseToStructureId(null);
                }}
              >
                Annulla
              </Button>
              <Button 
                className="flex-1 bg-blue-700 hover:bg-blue-800"
                onClick={handleAddHouseToExisting}
              >
                Aggiungi Casa
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Role */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Ruolo - {editingUser?.username}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={editUserRole} onValueChange={setEditUserRole}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin_struttura">Admin</SelectItem>
                <SelectItem value="operatore">Operatore</SelectItem>
                <SelectItem value="cucina">Cucina</SelectItem>
              </SelectContent>
            </Select>
            <Button className="w-full bg-blue-700 hover:bg-blue-800" onClick={handleUpdateUserRole}>
              Salva
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Organization Dialog */}
      <Dialog open={assignOrgDialogOpen} onOpenChange={setAssignOrgDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assegna Organizzazione</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Seleziona l'organizzazione per <strong>{structureForOrg?.name}</strong>
            </p>
            <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
              <SelectTrigger>
                <SelectValue placeholder="Seleziona organizzazione..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Nessuna organizzazione</SelectItem>
                {organizations.map(org => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name} ({org.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setAssignOrgDialogOpen(false)}
              >
                Annulla
              </Button>
              <Button 
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                onClick={handleAssignOrganization}
              >
                <Briefcase className="w-4 h-4 mr-2" />
                Assegna
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Production Type Dialog */}
      <Dialog open={productionTypeDialogOpen} onOpenChange={setProductionTypeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tipo Produzione</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Seleziona il tipo di produzione per <strong>{structureForProductionType?.name}</strong>
            </p>
            <Select value={selectedProductionType} onValueChange={setSelectedProductionType}>
              <SelectTrigger>
                <SelectValue placeholder="Seleziona tipo..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="own">
                  <div className="flex items-center gap-2">
                    <ChefHat className="w-4 h-4" />
                    Produzione Propria
                  </div>
                </SelectItem>
                <SelectItem value="catering">
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Solo Catering (serve altre strutture)
                  </div>
                </SelectItem>
                <SelectItem value="both">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Produzione + Catering
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            
            <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
              {selectedProductionType === 'own' && (
                <p><strong>Produzione Propria:</strong> La struttura cucina internamente i pasti.</p>
              )}
              {selectedProductionType === 'catering' && (
                <p><strong>Solo Catering:</strong> La struttura produce pasti per altre strutture (non ha produzione interna).</p>
              )}
              {selectedProductionType === 'both' && (
                <p><strong>Produzione + Catering:</strong> La struttura cucina internamente e può anche servire altre strutture.</p>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setProductionTypeDialogOpen(false)}
              >
                Annulla
              </Button>
              <Button 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={handleChangeProductionType}
              >
                <Truck className="w-4 h-4 mr-2" />
                Salva
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Catering Relation Dialog */}
      <Dialog open={cateringRelationDialogOpen} onOpenChange={setCateringRelationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Collega a Catering</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Seleziona la struttura catering che servirà <strong>{satelliteForCatering?.name}</strong>
            </p>
            
            {cateringStructures.length === 0 ? (
              <div className="bg-amber-50 p-4 rounded-lg text-amber-700 text-sm">
                <p>Nessuna struttura catering disponibile.</p>
                <p className="mt-1">Prima imposta una struttura come "Catering" o "Produzione + Catering".</p>
              </div>
            ) : (
              <Select value={selectedCateringId} onValueChange={setSelectedCateringId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona catering..." />
                </SelectTrigger>
                <SelectContent>
                  {cateringStructures.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-emerald-600" />
                        {s.name}
                        {s.organization_name && (
                          <span className="text-xs text-slate-400">({s.organization_name})</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setCateringRelationDialogOpen(false)}
              >
                Annulla
              </Button>
              <Button 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={handleCreateCateringRelation}
                disabled={!selectedCateringId}
              >
                <UtensilsCrossed className="w-4 h-4 mr-2" />
                Collega
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Room Configuration */}
      <RoomConfigDialog
        open={configDialogOpen}
        onOpenChange={setConfigDialogOpen}
        structure={selectedStructure}
        onSave={handleSaveRoomConfig}
      />
    </div>
  );
};

// Room Configuration Dialog
const RoomConfigDialog = ({ open, onOpenChange, structure, onSave }) => {
  const [selectedHouseId, setSelectedHouseId] = useState(null);
  const [floors, setFloors] = useState([]);
  const [roomsConfig, setRoomsConfig] = useState({});
  const [newFloor, setNewFloor] = useState('');
  const [newRoomInput, setNewRoomInput] = useState({});

  // Get selected house data for multi-casa structures
  const selectedHouse = structure?.is_multi_house && selectedHouseId
    ? structure.houses?.find(h => h.id === selectedHouseId)
    : null;

  useEffect(() => {
    if (structure) {
      if (structure.is_multi_house) {
        // For multi-casa, select first house by default
        if (structure.houses?.length > 0 && !selectedHouseId) {
          setSelectedHouseId(structure.houses[0].id);
        }
        // Load selected house data
        if (selectedHouse) {
          setFloors(selectedHouse.floors || []);
          setRoomsConfig(selectedHouse.rooms_config || {});
        }
      } else {
        // Single-casa structure
        setFloors(structure.floors || []);
        setRoomsConfig(structure.rooms_config || {});
      }
    }
  }, [structure, selectedHouseId, selectedHouse]);

  // Reset selection when dialog opens with new structure
  useEffect(() => {
    if (open && structure?.is_multi_house && structure.houses?.length > 0) {
      setSelectedHouseId(structure.houses[0].id);
    }
  }, [open, structure]);

  const handleAddFloor = () => {
    if (!newFloor.trim() || floors.includes(newFloor.trim())) return;
    const floor = newFloor.trim();
    setFloors([...floors, floor]);
    setRoomsConfig({ ...roomsConfig, [floor]: [] });
    setNewFloor('');
  };

  const handleAddRoom = (floor) => {
    const input = newRoomInput[floor]?.trim();
    if (!input) return;
    
    const currentRooms = roomsConfig[floor] || [];
    let newRooms = [];
    
    const parts = input.split(',').map(p => p.trim()).filter(Boolean);
    for (const part of parts) {
      if (part.includes('-')) {
        const [start, end] = part.split('-').map(s => s.trim());
        const startMatch = start.match(/^(\D*)(\d+)$/);
        const endMatch = end.match(/^(\D*)(\d+)$/);
        
        if (startMatch && endMatch) {
          const prefix = startMatch[1] || '';
          const startNum = parseInt(startMatch[2], 10);
          const endNum = parseInt(endMatch[2], 10);
          const numLength = startMatch[2].length;
          
          for (let i = startNum; i <= endNum; i++) {
            const room = prefix + String(i).padStart(numLength, '0');
            if (!currentRooms.includes(room) && !newRooms.includes(room)) {
              newRooms.push(room);
            }
          }
        }
      } else {
        if (!currentRooms.includes(part) && !newRooms.includes(part)) {
          newRooms.push(part);
        }
      }
    }
    
    if (newRooms.length > 0) {
      setRoomsConfig({ ...roomsConfig, [floor]: [...currentRooms, ...newRooms].sort() });
    }
    setNewRoomInput({ ...newRoomInput, [floor]: '' });
  };

  if (!structure) return null;

  const handleSave = () => {
    if (structure.is_multi_house) {
      onSave(floors, roomsConfig, selectedHouseId);
    } else {
      onSave(floors, roomsConfig, null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Configura Camere - {structure.name}
            {selectedHouse && ` (${selectedHouse.prefix} - ${selectedHouse.name})`}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* House selector for multi-casa structures */}
          {structure.is_multi_house && structure.houses?.length > 0 && (
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <label className="text-sm font-medium text-purple-700 mb-2 block">Seleziona Casa</label>
              <Select value={selectedHouseId || ''} onValueChange={setSelectedHouseId}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Seleziona casa..." />
                </SelectTrigger>
                <SelectContent>
                  {structure.houses.map(house => (
                    <SelectItem key={house.id} value={house.id}>
                      <div className="flex items-center gap-2">
                        <Home className="w-4 h-4" />
                        <span className="font-medium">{house.prefix}</span>
                        <span>- {house.name}</span>
                        <span className="text-slate-500 text-xs ml-2">
                          ({house.floors?.length || 0} piani, {Object.values(house.rooms_config || {}).reduce((a, r) => a + (r?.length || 0), 0)} camere)
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex gap-2">
            <Input
              value={newFloor}
              onChange={(e) => setNewFloor(e.target.value)}
              placeholder="Nuovo piano (es: 3)"
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFloor())}
            />
            <Button onClick={handleAddFloor}><Plus className="w-4 h-4" /></Button>
          </div>

          {floors.map(floor => (
            <div key={floor} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">Piano {floor}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFloors(floors.filter(f => f !== floor));
                    const newConfig = { ...roomsConfig };
                    delete newConfig[floor];
                    setRoomsConfig(newConfig);
                  }}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-1 mb-3">
                {(roomsConfig[floor] || []).map(room => (
                  <Badge key={room} variant="secondary" className="text-xs">
                    {room}
                    <button
                      onClick={() => setRoomsConfig({
                        ...roomsConfig,
                        [floor]: roomsConfig[floor].filter(r => r !== room)
                      })}
                      className="ml-1 hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>

              <div className="flex gap-2">
                <Input
                  value={newRoomInput[floor] || ''}
                  onChange={(e) => setNewRoomInput({ ...newRoomInput, [floor]: e.target.value })}
                  placeholder="101-110 o 101, 102, 103"
                  className="flex-1 text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddRoom(floor))}
                />
                <Button variant="outline" size="sm" onClick={() => handleAddRoom(floor)}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}

          <Button onClick={handleSave} className="w-full bg-blue-700 hover:bg-blue-800">
            Salva
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
